if (!customElements.get('custom-bundle')) {
  customElements.define(
    'custom-bundle',
    class CustomBundle extends HTMLElement {
      constructor() {
        super();
        try {
          this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
        } catch (e) {
          console.error('[custom-bundle] failed to parse bundle_configuration', e);
          this.config = { components: [], discount: null };
        }
        try {
          this.variantsByProduct = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
        } catch (e) {
          console.error('[custom-bundle] failed to parse bundle_variants', e);
          this.variantsByProduct = {};
        }

        // O(1) variant lookup map: productGid → { variantId → variant }
        this.variantsById = {};
        Object.keys(this.variantsByProduct).forEach((gid) => {
          this.variantsById[gid] = Object.fromEntries(
            this.variantsByProduct[gid].variants.map((v) => [v.id, v])
          );
        });

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.atcButton = this.querySelector('[data-bundle-atc]');
        this.errorEl = this.querySelector('[data-bundle-error]');

        // Every variant of every child product is offered in the bundle —
        // the merchant app no longer emits a per-component variantIds filter
        // (always "all variants", and storing the list ate too much room
        // against the Cart Transform metafield-size limits). Bootstrap picks
        // the first available variant per child, falling back to the first
        // variant if the whole product is sold out.
        this.state = (this.config.components || []).map((c) => {
          const productData = this.variantsByProduct[c.productId];
          const variants = productData?.variants || [];
          const initial = variants.find((v) => v.available) || variants[0];
          // Per-component quantity (defaults to 1 for legacy bundles that don't
          // emit the field yet). Coerced and floored at 1 to guard against
          // bad/string values written by the merchant app.
          const qty = Math.max(1, parseInt(c.quantity, 10) || 1);
          return {
            productId: c.productId,
            selectedVariantId: initial ? initial.id : null,
            quantity: qty,
          };
        });
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        if (this.atcButton) this.atcButton.addEventListener('click', this.onAddToCart.bind(this));
        // Resolve the wrapper product's price block (rendered by snippets/price.liquid
        // inside sections/main-product.liquid). We overwrite it with a total derived
        // from the selected child variants + the bundle discount, so the PDP price
        // matches what Cart Transform actually charges instead of the static price
        // set on the wrapper product. The bundle wrapper has only a default variant,
        // so no other script (VariantSelects/section-render) ever touches this block.
        this.priceBlock = document.getElementById('price-' + this.dataset.sectionId);
        this.currencySuffix = this.detectCurrencySuffix();
        // Liquid renders the correct initial selection per child; only OOS state needs
        // computing (depends on cross-option availability not knowable in Liquid).
        this.recompute();
        // Apply initial data-mirror per child based on the default-checked extras.
        this.state.forEach((_, idx) => this.syncChildMirror(idx));
      }

      // Sets data-mirror on a child's product image based on currently-checked
      // extra options (Left/Lewa = mirrored). CSS handles the actual transform,
      // including the per-product-handle invert for naroznik-240cm and
      // regal-z-biurkiem (assets photographed opposite).
      syncChildMirror(childIdx) {
        const childEl = this.querySelector(`[data-child-index="${childIdx}"]`);
        if (!childEl) return;
        const img = childEl.querySelector(`.custom-bundle__product-image[data-bundle-image="${childIdx}"]`);
        if (!img) return;
        const checkedValues = Array.from(
          childEl.querySelectorAll('[data-bundle-extras] input[type="radio"]:checked')
        ).map((r) => r.value);
        const isMirrored = checkedValues.includes('Left') || checkedValues.includes('Lewa');
        img.dataset.mirror = String(isMirrored);
      }

      onClick(evt) {
        const toggle = evt.target.closest('[data-bundle-toggle]');
        if (toggle) this.toggleAccordion(parseInt(toggle.dataset.bundleToggle, 10));
      }

      onChange(evt) {
        if (evt.target.matches('[data-bundle-swatch]')) return this.onSwatchChange(evt.target);
        if (evt.target.matches('[data-bundle-extras] input[type="radio"]')) return this.onExtraChange(evt.target);
      }

      // Extra-option (Konfiguracja, etc.) selection — line-item-property only,
      // doesn't change the variant. Refreshes the heading value span and
      // re-syncs the child product image's data-mirror flag.
      onExtraChange(input) {
        const childEl = input.closest('[data-child-index]');
        if (!childEl) return;
        const childIdx = parseInt(childEl.dataset.childIndex, 10);

        // Refresh the value span in the bundle-style heading; use the radio's
        // <label title> so the value renders Polish-translated (Lewa/Prawy)
        // instead of the raw English Left/Right.
        const labelTitle = childEl.querySelector(`label[for="${input.id}"]`)?.title || input.value;
        const valueEls = childEl.querySelectorAll(`[data-selected-swatch-value="${input.name}"]`);
        valueEls.forEach((el) => { el.textContent = labelTitle; });

        this.syncChildMirror(childIdx);
      }

      toggleAccordion(idx) {
        const accordionEl = this.querySelector(`[data-child-index="${idx}"]`);
        const bodyEl = this.querySelector(`[data-bundle-body="${idx}"]`);
        const headerEl = this.querySelector(`[data-bundle-toggle="${idx}"]`);
        const badgeEl = this.querySelector(`[data-bundle-count="${idx}"]`);
        if (!accordionEl || !bodyEl) return;
        const willOpen = !accordionEl.classList.contains('custom-bundle__accordion--open');
        accordionEl.classList.toggle('custom-bundle__accordion--open', willOpen);
        if (badgeEl) badgeEl.classList.toggle('custom-bundle__count-badge--active', willOpen);
        if (headerEl) headerEl.setAttribute('aria-expanded', String(willOpen));
        bodyEl.hidden = !willOpen;
      }

      onSwatchChange(input) {
        const childEl = input.closest('[data-child-index]');
        if (!childEl) return;
        const childIdx = parseInt(childEl.dataset.childIndex, 10);
        const child = this.state[childIdx];
        const productData = this.variantsByProduct[child?.productId];
        if (!child || !productData) return;

        const groups = this.getGroups(childEl);
        const selectedOptions = this.readSelectedOptions(groups);

        const match = productData.variants.find((v) =>
          selectedOptions.every((val, i) => val === null || (v.options[i] || null) === val)
        );

        if (match) {
          child.selectedVariantId = match.id;
        } else {
          // No exact match for the new combo; pick first variant matching the
          // just-changed option, preferring available ones.
          const optionIdx = groups.indexOf(input.closest('[data-option-position]'));
          const matchingOption = productData.variants.filter(
            (v) => (v.options[optionIdx] || null) === input.value
          );
          const fallback = matchingOption.find((v) => v.available) || matchingOption[0];
          if (fallback) child.selectedVariantId = fallback.id;
        }

        this.syncChildBody(childIdx);
        this.recompute();
      }

      findVariant(childIdx) {
        const child = this.state[childIdx];
        const map = this.variantsById[child?.productId];
        return map ? map[child.selectedVariantId] || null : null;
      }

      getGroups(childEl) {
        return Array.from(childEl.querySelectorAll('[data-option-position]'));
      }

      readSelectedOptions(groups) {
        return groups.map((g) => {
          const checked = g.querySelector('input[type="radio"]:checked');
          return checked ? checked.value : null;
        });
      }

      syncChildBody(childIdx) {
        const variant = this.findVariant(childIdx);
        const childEl = this.querySelector(`[data-child-index="${childIdx}"]`);
        if (!variant || !childEl) return;

        if (variant.image) {
          const img = childEl.querySelector(`[data-bundle-image="${childIdx}"]`);
          if (img && img.src !== variant.image) img.src = variant.image;
        }

        this.getGroups(childEl).forEach((group, optionIdx) => {
          const value = variant.options[optionIdx];
          if (value == null) return;

          const targetRadio = group.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (targetRadio && !targetRadio.checked) targetRadio.checked = true;

          group.querySelectorAll('.custom-bundle__swatch').forEach((sw) => {
            const radio = sw.querySelector('input[type="radio"]');
            sw.classList.toggle('custom-bundle__swatch--selected', !!(radio && radio.checked));
          });

          const headingValue = group.querySelector(`[data-bundle-option-selected="${childIdx}_${optionIdx + 1}"]`);
          if (headingValue) headingValue.textContent = value;

          const infoValue = group.querySelector(`[data-bundle-info-value="${childIdx}_${optionIdx + 1}"]`);
          if (infoValue) infoValue.textContent = value;

          // Stretch banner: prefer hi-res URL on the radio's data-stretch-src;
          // fall back to the swatch image src (lower-res).
          const checkedRadio = group.querySelector('input[type="radio"]:checked');
          const stretchImg = group.querySelector(`[data-bundle-stretch="${childIdx}_${optionIdx + 1}"]`);
          if (stretchImg) {
            const hiRes = checkedRadio && checkedRadio.dataset.stretchSrc;
            const fallbackImg = group.querySelector('.custom-bundle__swatch--selected .custom-bundle__swatch-img');
            const newSrc = hiRes || (fallbackImg && fallbackImg.tagName === 'IMG' ? fallbackImg.src : '');
            if (newSrc && stretchImg.src !== newSrc) stretchImg.src = newSrc;
            stretchImg.alt = value;
          }
        });
      }

      updateSwatchAvailability(childIdx) {
        const child = this.state[childIdx];
        const productData = this.variantsByProduct[child?.productId];
        const childEl = this.querySelector(`[data-child-index="${childIdx}"]`);
        if (!productData || !childEl) return;

        const groups = this.getGroups(childEl);
        const selectedOptions = this.readSelectedOptions(groups);

        // Hierarchical OOS logic mirroring Fly Bundles:
        //  - Option 0 (primary cluster selector) is permissive: a value is OOS
        //    only if no available variant has it at index 0 at all. Prevents
        //    the deadlock where every value in a disjoint cluster becomes
        //    unreachable.
        //  - Options 1+ are conditioned on prior levels: a value is OOS unless
        //    an available variant exists where options[i] === selected[i] for
        //    every i < optionIdx AND options[optionIdx] === input.value. Later
        //    options (i > optionIdx) are NOT constrained.
        // Click is always allowed; the slash is purely visual. The fallback
        // in onSwatchChange handles "no exact match" by switching to the
        // closest reachable variant.
        groups.forEach((group, optionIdx) => {
          group.querySelectorAll('[data-bundle-swatch]').forEach((input) => {
            const reachable = productData.variants.some((v) => {
              if (!v.available) return false;
              if (v.options[optionIdx] !== input.value) return false;
              for (let i = 0; i < optionIdx; i++) {
                if (selectedOptions[i] !== null && v.options[i] !== selectedOptions[i]) return false;
              }
              return true;
            });
            const swatchEl = input.closest('.custom-bundle__swatch');
            if (swatchEl) swatchEl.classList.toggle('custom-bundle__swatch--oos', !reachable);
          });
        });
      }

      recompute() {
        // Enables/disables the ATC button, refreshes per-child OOS state, and (via
        // updatePrice below) repaints the wrapper price block from the selected child
        // variants + bundle discount. Cart Transform still applies the real discount at
        // checkout via the _bundle_identifier line item property; the price block is a
        // display preview kept in sync with that rule.
        let allAvailable = true;
        this.state.forEach((_, i) => {
          const v = this.findVariant(i);
          if (!v || !v.available) allAvailable = false;
          this.updateSwatchAvailability(i);
        });
        if (this.atcButton) this.atcButton.disabled = !allAvailable;
        this.updatePrice();
      }

      // Sum of the currently-selected child variant prices (in cents, undiscounted).
      // Returns null if any child variant fails to resolve (deleted/unpublished
      // child, or a bad selection) so callers fall back to the server-rendered
      // price instead of silently dropping that child's price from the total.
      childrenTotal() {
        let sum = 0;
        for (let i = 0; i < this.state.length; i++) {
          const v = this.findVariant(i);
          if (!v || typeof v.price !== 'number') return null;
          sum += v.price * this.state[i].quantity;
        }
        return sum;
      }

      // Applies the bundle discount (config.discount) to a cents total, mirroring
      // the Cart Transform rule. Only percentage_off is emitted by the app today
      // and is the only type whose math we can mirror exactly, so any other type
      // returns null -> caller falls back to the server-rendered wrapper price
      // rather than showing an undiscounted (higher-than-checkout) total. No
      // discount at all returns the full total unchanged.
      applyBundleDiscount(cents) {
        const d = this.config.discount;
        if (!d) return cents;
        if (d.type !== 'percentage_off') return null;
        let value = parseFloat(d.value);
        if (!isFinite(value)) return null;
        value = Math.min(100, Math.max(0, value));
        // round(full * (100 - value) / 100) — same order as the Liquid side so
        // the JS repaint matches the server first paint to the cent.
        return Math.round((cents * (100 - value)) / 100);
      }

      // Reads the trailing non-numeric part of a server-rendered price (e.g. " zł")
      // so the currency symbol stays in sync if it ever changes, without hardcoding.
      detectCurrencySuffix() {
        const sample = this.priceBlock?.querySelector('.price-item')?.textContent || '';
        const match = sample.match(/[^\d]*$/);
        return (match && match[0]) || ' zł';
      }

      // Formats a cents amount the way the shop's `money_without_trailing_zeros`
      // filter does on this storefront: '.' thousands separator, ',' decimals,
      // trailing ,00 dropped when the amount is whole (PLN). This assumes
      // settings.currency_code_enabled is off (it is on Beluro), so price.liquid
      // also renders via money_without_trailing_zeros and the two stay in sync.
      // If currency codes are ever enabled the server switches to
      // money_with_currency (keeps ,00 + a code) and this would need to match.
      formatMoney(cents) {
        cents = Math.round(cents);
        const whole = Math.floor(cents / 100);
        const frac = cents % 100;
        let out = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        if (frac !== 0) out += ',' + String(frac).padStart(2, '0');
        return out + this.currencySuffix;
      }

      // Overwrites the wrapper price block with the children-derived total:
      //   bold price  = discounted total (what Cart Transform charges)
      //   struck price = full total (sum of children, no discount)
      // The percentage discount is constant across variant selections, so the
      // server-rendered "-N%" badge stays correct and is left untouched.
      updatePrice() {
        if (!this.priceBlock) return;
        const priceEl = this.priceBlock.querySelector('.price');
        if (!priceEl) return;

        const full = this.childrenTotal();
        // Bail out and keep the server-rendered price when the total can't be
        // computed reliably (missing child) or the discount type is unknown —
        // Liquid falls back to the wrapper price in exactly these cases, so
        // leaving its value untouched keeps the two in sync.
        if (full == null || full <= 0) return;
        const discounted = this.applyBundleDiscount(full);
        if (discounted == null || discounted <= 0) return;
        const onSale = discounted < full;

        priceEl.classList.toggle('price--on-sale', onSale);
        priceEl.setAttribute('data-value', String(Math.round(discounted)));

        const fullMoney = this.formatMoney(full);
        const discountedMoney = this.formatMoney(discounted);

        // .price__sale block (shown while on sale): bold sale + struck compare.
        priceEl.querySelectorAll('.price__sale .price-item--sale').forEach((el) => {
          el.textContent = discountedMoney;
        });
        priceEl.querySelectorAll('.price__sale s.price-item--regular').forEach((el) => {
          el.textContent = fullMoney;
        });
        // .price__regular bold span (shown only if a bundle ever has no discount).
        priceEl.querySelectorAll('.price__regular span.price-item--regular').forEach((el) => {
          el.textContent = onSale ? fullMoney : discountedMoney;
        });
      }

      async onAddToCart() {
        if (!this.atcButton || this.atcButton.disabled) return;
        this.hideError();
        this.atcButton.disabled = true;
        this.atcButton.classList.add('loading');

        const items = this.state.map((s, idx) => {
          const extras = this.readChildExtras(idx);
          const properties = {
            _bundle_identifier: this.config.identifier,
            ...extras,
          };
          // Mirror the standalone PDP behavior (global.js updateVariantSku):
          // attach the child's variant SKU as a hidden _SKU line-item property
          // so bundle line items carry the same fulfillment data as products
          // bought individually.
          const sku = this.childSku(idx, extras);
          if (sku) properties._SKU = sku;
          return {
            id: s.selectedVariantId,
            quantity: s.quantity,
            properties,
          };
        });

        const body = { items };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sectionIds = this.cart.getSectionsToRender().map((s) => s.id);
          if (sectionIds.length) {
            body.sections = sectionIds;
            body.sections_url = window.location.pathname;
          }
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        try {
          const res = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();

          if (!res.ok || data.status) {
            const msg = data.description || data.message || 'Add to cart failed';
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'custom-bundle',
              productVariantId: items[0] ? items[0].id : null,
              errors: data.errors || data.description,
              message: data.message,
            });
            this.showError(msg);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-bundle',
            productVariantId: items[0] ? items[0].id : null,
            cartData: data,
          });

          if (this.cart) {
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (typeof this.cart.renderContents === 'function') this.cart.renderContents(data);
          }
        } catch (e) {
          console.error('[custom-bundle] ATC error', e);
          this.showError('Network error');
        } finally {
          this.atcButton.classList.remove('loading');
          this.atcButton.disabled = false;
          this.recompute();
        }
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      // Reads per-child extra-option radios (Konfiguracja, Podstawa, etc.) and
      // returns a flat { propertyKey: value } map for spreading into the child's
      // items[i].properties. Empty when the child has no matching extras.
      readChildExtras(childIdx) {
        const childEl = this.querySelector(`[data-child-index="${childIdx}"]`);
        const extrasEl = childEl?.querySelector('[data-bundle-extras]');
        if (!extrasEl) return {};

        const out = {};
        extrasEl.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
          // Radio name is 'properties[<labelCart>]' — strip the wrapper so the
          // key in items[i].properties is just '<labelCart>'.
          const m = radio.name.match(/^properties\[(.+)\]$/);
          if (m) out[m[1]] = radio.value;
        });
        return out;
      }

      // Computes the _SKU line-item property, mirroring the standalone PDP
      // (global.js updateMetavariant + renderMetavariantInfo): match the
      // custom.variant_image_sets metaobject by option_values against the
      // selected values (native variant options + extra fields, /pl/-mapped) and
      // take its sku. The L/R orientation rewrite is copied 1:1 from global.js.
      //
      // Deliberate divergence: we ALSO fall back to the native variant.sku when
      // no metavariant SKU matches. Products like sofas have variant.sku = null
      // and get their SKU from the metaobject; komodas/szafka are the opposite
      // (metavariant.sku is null, real SKU on the variant). The fallback lets
      // both carry _SKU on bundle lines. The PDP doesn't need it because Shopify
      // already surfaces the native variant SKU on the order for those products.
      childSku(idx, extras) {
        const variant = this.findVariant(idx);
        const child = this.state[idx];
        const productData = this.variantsByProduct[child?.productId];
        const metavariants = (productData && productData.metavariants) || [];

        // Selected values = native variant options + extra-field values, all run
        // through the same /pl/ mapping the PDP applies to this.options.
        const selectedValues = [
          ...((variant && variant.options) || []),
          ...Object.values(extras || {}),
        ]
          .filter((v) => v != null)
          .map((v) => this.mapPlOption(v));

        const match = metavariants.find(
          (mv) =>
            Array.isArray(mv.option_values) &&
            mv.option_values.every((o) => selectedValues.includes(o.value))
        );

        // Metavariant-SKU path: apply the L/R orientation rewrite HERE ONLY,
        // exactly like the PDP (global.js renderMetavariantInfo rewrites inside
        // `if (sku)`, where sku comes from the metaobject). On the /pl/ storefront
        // orientation values are Polish ('Lewa'/'Prawa'), so this branch is dead —
        // same as the PDP; orientation travels on the Konfiguracja line-item
        // property, not the SKU.
        let sku = match?.sku || '';
        if (sku) {
          const orientation = selectedValues.find((v) => v === 'Left' || v === 'Right');
          if (orientation) sku = sku.replace(/[LR]/, orientation.charAt(0));
        }

        // Native-SKU fallback for komodas/szafka (metavariant.sku is null). The
        // PDP never rewrites native variant SKUs, so we must NOT either — a real
        // SKU that happens to contain an L or R would otherwise be corrupted.
        if (!sku && variant && variant.sku) sku = variant.sku;
        return sku;
      }

      // Polish translation for infinite-option values, matching global.js
      // updateSelectedOptions so extra-field values line up with the metaobject
      // option_values (which are stored in Polish on the /pl/ storefront).
      mapPlOption(option) {
        if (typeof Shopify === 'undefined' || !Shopify.routes || Shopify.routes.root !== '/pl/') {
          return option;
        }
        switch (option) {
          case 'Block':
            return 'Blok';
          case 'Legs':
            return 'Nogi';
          case 'Shelf':
            return 'Półka';
          case 'Sleeper':
            return 'Funkcja spania';
          default:
            return option;
        }
      }
    }
  );
}
